import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatApiError } from "@/lib/api";
import { SENIORITY_OPTIONS, getOrderedCapabilities, CAPABILITY_OPTIONS } from "@/lib/intake-data";
import RevealRenderer, { TimeModeToggle } from "@/components/RevealRenderer";
import LoadingReveal from "@/components/LoadingReveal";
import AccountFollowList from "@/components/AccountFollowList";
import RecalibratingOverlay from "@/components/RecalibratingOverlay";
import RecalibrationBanner from "@/components/RecalibrationBanner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BetaBadge } from "@/components/BetaBadge";
import { KalibLogo } from "@/components/KalibLogo";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Settings, LogOut, ArrowRight, User, KeyRound } from "lucide-react";
import { toast } from "sonner";

// v6.5.1 — Time-mode picker hidden behind a feature flag (NOT removed) per
// product request. The picker still mounts conditionally; persisted user
// preferences via /api/me/time-mode and the renderer's mode-aware logic
// remain intact in the component tree below. Flip to `true` to restore the
// "How much time do you have?" segmented control above the account tabs.
const SHOW_TIME_MODE_TOGGLE = false;

// Wave 10.3 — account-id to display name map
const ACCOUNT_ID_TO_NAME = {
  deutsche_bank: "Deutsche Bank",
  jpmorgan_chase: "JPMorgan Chase",
  goldman_sachs: "Goldman Sachs",
  citigroup: "Citigroup",
  lloyds_banking_group: "Lloyds Banking Group",
};

// 2026-04-29 — labels mirror the spec'd UI copy (Software / SaaS, Services /
// implementation, etc.). Backend enum values stay as-is. Legacy
// `technology_operations` is mapped to "Services / implementation".
const COMPANY_TYPE_LABELS = {
  software: "Software / SaaS",
  services: "Services / implementation",
  cloud: "Cloud / hyperscaler",
  advisory: "Advisory / consulting",
  technology_operations: "Services / implementation",
};

const ROLE_FAMILY_LABELS = {
  acquisition: "Win new business",
  retention: "Grow existing business",
  solutioning: "Build solutioning & commercials",
  delivery: "Deliver existing business",
  // Legacy `leadership` mapped to closest current motion.
  leadership: "Grow existing business",
};

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function hasLiveInSelection(selectedIds, catalog) {
  if (!Array.isArray(selectedIds) || !selectedIds.length) return false;
  return catalog.some((a) => a.status === "live" && selectedIds.includes(a.id));
}

function formatWeekRange(generationCycle) {
  if (!generationCycle) return null;
  const datePart = generationCycle.split("-tuesday")[0] || generationCycle;
  const tue = new Date(datePart + "T00:00:00Z");
  if (isNaN(tue.getTime())) return null;
  const mon = new Date(tue);
  mon.setUTCDate(mon.getUTCDate() + 6);
  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const tueDay = tue.getUTCDate();
  const monDay = mon.getUTCDate();
  if (tue.getUTCMonth() === mon.getUTCMonth()) {
    return `Week of ${tueDay}\u2013${monDay} ${months[tue.getUTCMonth()]} ${tue.getUTCFullYear()}`;
  }
  return `Week of ${tueDay} ${months[tue.getUTCMonth()]}\u2013${monDay} ${months[mon.getUTCMonth()]} ${tue.getUTCFullYear()}`;
}

const SENIORITY_LABELS = {
  individual: "Individual Contributor",
  // v5.2: backend `leader` is UI "Manager"; backend `pl_owner` is UI "Leader".
  leader: "Manager",
  pl_owner: "Leader",
};

// Chunk 1 / Task 7 — short-form labels for the lens breadcrumb
const BREADCRUMB_TYPE_LABELS = {
  software: "Software",
  services: "Services",
  cloud: "Cloud",
  advisory: "Advisory",
  technology_operations: "Services",
};

function formatHarvestAge(isoString) {
  if (!isoString) return null;
  const harvestDate = new Date(isoString);
  if (isNaN(harvestDate.getTime())) return null;
  const diffMs = Date.now() - harvestDate.getTime();
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffHours < 1) return "Updated just now";
  if (diffHours < 24) return `Updated ${diffHours}h ago`;
  if (diffDays === 1) return "Updated yesterday";
  if (diffDays < 7) return `Updated ${diffDays} days ago`;
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks === 1) return "Updated 1 week ago";
  return `Updated ${diffWeeks} weeks ago`;
}

// Chunk 3.9 (2026-05-05) — single canonical reveal-freshness source.
// Both the relative chip ("Updated yesterday") and the absolute strip
// label ("Updated 4 May") must read from the SAME timestamp so they
// cannot contradict. Priority order:
//   1. revealData.reveal_date          (synthesis ISO timestamp)
//   2. revealData.generated_at         (legacy field name)
//   3. revealData.generation_cycle     (e.g. "2026-05-04-tuesday")
// Falls back gracefully if the higher-priority field is missing.
function resolveRevealFreshnessIso(revealData) {
  if (!revealData) return null;
  if (revealData.reveal_date) return revealData.reveal_date;
  if (revealData.generated_at) return revealData.generated_at;
  if (revealData.generation_cycle) {
    const datePart = revealData.generation_cycle.split("-tuesday")[0] || revealData.generation_cycle;
    // Treat cycle date as midnight UTC on that date.
    return `${datePart}T00:00:00Z`;
  }
  return null;
}

function InlineCountdown({ nextCycle }) {
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    if (!nextCycle) return;
    const update = () => {
      const diff = new Date(nextCycle) - new Date();
      if (diff <= 0) { setTimeLeft("Generating now..."); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setTimeLeft(`${d}d : ${String(h).padStart(2, "0")}h : ${String(m).padStart(2, "0")}m`);
    };
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [nextCycle]);

  if (!timeLeft) return null;
  return (
    <span className="text-[13px] text-zinc-900 font-mono whitespace-nowrap" data-testid="countdown-timer">
      Next Reveal update in {timeLeft}
    </span>
  );
}

export default function IntelligenceView() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [revealData, setRevealData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAnimation, setShowAnimation] = useState(false);
  const [error, setError] = useState("");

  // Stage 2
  const [seniorityTier, setSeniorityTier] = useState("");
  const [capabilityBets, setCapabilityBets] = useState([]);
  const [stage2Loading, setStage2Loading] = useState(false);

  // Wave 8 — account interest
  const [accountsCatalog, setAccountsCatalog] = useState([]);
  const [followedAccounts, setFollowedAccounts] = useState([]); // hydrated from /intelligence/reveal once user data loads
  const [showFollowModal, setShowFollowModal] = useState(false);
  const [modalFollowed, setModalFollowed] = useState(["deutsche_bank"]);
  const [modalSaving, setModalSaving] = useState(false);
  const [modalError, setModalError] = useState("");

  // Wave 9.1 — recalibration overlay + banner
  const [recalibEvent, setRecalibEvent] = useState(null);
  const [showRecalibOverlay, setShowRecalibOverlay] = useState(false);

  // Wave 10.3 — selected account id (drives the reveal endpoint + tabs)
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [initialAccountResolved, setInitialAccountResolved] = useState(false);

  // Per-session reveal cache. Keyed by accountId, holds the last successful
  // FULL reveal response so toggling between previously-loaded account tabs
  // is instant — no re-fetch, no "Calibrating for you…" skeleton flash.
  // Cache lives in a ref (no re-renders on write) and resets naturally on
  // hard refresh or component remount (e.g. after a lens recalibration,
  // which navigates from /settings back to /intelligence and forces a
  // fresh mount). No explicit eviction needed.
  const revealCacheRef = useRef({});

  // Time mode
  const [timeMode, setTimeMode] = useState(() => {
    try { return localStorage.getItem("kalib_time_mode") || "coffee"; } catch { return "coffee"; }
  });
  const handleModeChange = (m) => {
    setTimeMode(m);
    try { localStorage.setItem("kalib_time_mode", m); } catch {}
  };

  const fetchReveal = useCallback(async (accountId) => {
    try {
      const accId = accountId || selectedAccountId;
      if (!accId) return;

      // Wave 13 — parallel lite + full fetch for fast first paint.
      // Lite (~6 KB) renders immediately; full (~80 KB) hydrates persona-calibrated
      // content (matrix implications, seniority-specific options, feedback state).
      const litePromise = API.get(`/intelligence/reveal-lite?account=${accId}`).catch(() => null);
      const fullPromise = API.get(`/intelligence/reveal?account=${accId}`);

      const liteResp = await litePromise;
      if (liteResp?.data) {
        const lite = liteResp.data;
        setRevealData({
          is_lite: true,
          account_name: lite.account_name,
          signal_sentence: lite.signal_sentence,
          harvested_at: lite.harvested_at,
          generation_cycle: lite.generation_cycle,
          reveal_date: lite.reveal_date,
          full_reveal: JSON.stringify({
            signal_sentence: lite.signal_sentence,
            external_view_marker: lite.external_view_marker,
            account_signals: lite.account_signals,
            external_signals: lite.external_signals,
            actions: lite.actions,
          }),
        });
      }

      const { data } = await fullPromise;
      setRevealData(data);
      // Per-session reveal cache write. Stored under the account id
      // we just fetched for, so a subsequent tab switch back to this
      // account is instant — handleAccountSwitch reads this ref and
      // skips both lite + full network fetches on a hit.
      revealCacheRef.current[accId] = data;
      // Wave 8 — if existing stage2 user hasn't picked followed accounts, prompt one-time
      const existingFollowed = data?.intake?.followed_accounts;
      if ((data?.intake?.stage === 2) && (!existingFollowed || existingFollowed.length === 0)) {
        setShowFollowModal(true);
        setModalFollowed(["deutsche_bank"]);
      } else if (existingFollowed && existingFollowed.length) {
        setFollowedAccounts(existingFollowed);
      }
    } catch (err) {
      if (err.response?.status === 400) {
        navigate("/intake");
        return;
      }
      // v5.1 P1.1 — re-intake gate. Backend returns 409 when the user's
      // role_family is a deprecated value (e.g. legacy `leadership`).
      // Bounce them to /intake/re so they can pick a current value.
      if (err.response?.status === 409 && err.response?.data?.detail?.code === "requires_re_intake") {
        navigate("/intake/re");
        return;
      }
      // Chunk 1 / Task 4 — 503 calibration error: show soft state, not technical detail
      if (err.response?.status === 503 && err.response?.data?.detail?.code === "calibration_error") {
        setError("calibration_error");
        return;
      }
      setError(formatApiError(err));
    }
  }, [navigate, selectedAccountId]);

  // Wave 10.3 — refetch reveal when user switches account tab
  const handleAccountSwitch = async (accountId) => {
    if (accountId === selectedAccountId) return;
    setSelectedAccountId(accountId);

    // Cache hit — hydrate immediately from the prior full response. No
    // loading state, no lite-then-full flicker, no action-card skeletons.
    // The week's data doesn't change mid-session, so the cached payload
    // is correct until the user hard-refreshes or recalibrates.
    const cached = revealCacheRef.current[accountId];
    if (cached) {
      setRevealData(cached);
      return;
    }

    setLoading(true);
    try {
      await fetchReveal(accountId);
    } finally {
      setLoading(false);
    }
  };

  // Wave 8 — fetch account catalog
  useEffect(() => {
    API.get("/accounts").then(({ data }) => {
      setAccountsCatalog(data.accounts || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user === null) { navigate("/signin"); return; }
    if (user === undefined) return;
    if (user.intake_stage < 2) { navigate("/intake"); return; }

    // Wave 9.1 — check for pending recalibration event (set by SettingsPage.handleSave)
    let pendingRecalib = null;
    try {
      const raw = sessionStorage.getItem("kalib_recalib_event");
      if (raw) {
        pendingRecalib = JSON.parse(raw);
        sessionStorage.removeItem("kalib_recalib_event");
      }
    } catch {
      pendingRecalib = null;
    }

    if (pendingRecalib) {
      setRecalibEvent(pendingRecalib);
      setShowRecalibOverlay(true);
      // actual fetchReveal happens in the smart-default effect below once selectedAccountId is set
      return;
    }

    const seen = sessionStorage.getItem("kalib_reveal_seen");
    if (!seen) {
      setShowAnimation(true);
      const timer = setTimeout(() => {
        setShowAnimation(false);
        setLoading(false);
        sessionStorage.setItem("kalib_reveal_seen", "true");
      }, 12000);
      return () => clearTimeout(timer);
    }
  }, [user, navigate]);

  // Wave 10.3.1 — resolve initial account once catalog is available.
  // Prefer the user's first followed live account; fallback to first live account in catalog.
  useEffect(() => {
    if (initialAccountResolved) return;
    if (!user || user === undefined || user === null) return;
    if (user.intake_stage < 1) return;
    if (!accountsCatalog.length) return;

    const liveIds = accountsCatalog.filter((a) => a.status === "live").map((a) => a.id);
    if (!liveIds.length) return;

    // Read followed_accounts from the user's intake — we'll get it from fetched revealData.intake
    // or we can fetch intake directly here. Simplest: trigger a lightweight fetch to resolve.
    const pickDefault = async () => {
      let followed = [];
      try {
        const { data } = await API.get("/intake/responses");
        followed = Array.isArray(data?.followed_accounts) ? data.followed_accounts : [];
      } catch {
        // ignore — will fall back to first live in catalog
      }
      // Hydrate followedAccounts state as soon as we have the list, so the
      // 12s splash screen shows the correct account chips instead of the
      // default placeholder. fetchReveal() further down also sets this from
      // its own intake echo, but by then the loader has already been on
      // screen for several seconds.
      if (followed.length) setFollowedAccounts(followed);
      // Pick first followed that is live (preserve the user's follow order)
      let pick = followed.find((id) => liveIds.includes(id));
      if (!pick) pick = liveIds[0]; // fallback: first live account in catalog order
      // Deep-link override: ?account= from email or direct URL wins over default
      const urlAccountId = new URLSearchParams(location.search).get("account");
      if (urlAccountId && accountsCatalog.some((a) => a.id === urlAccountId)) {
        pick = urlAccountId;
      }
      setSelectedAccountId(pick);
      setInitialAccountResolved(true);

      // Trigger the initial fetch now
      try {
        await fetchReveal(pick);
      } finally {
        // If we didn't queue the 12s splash, flip loading off
        if (sessionStorage.getItem("kalib_reveal_seen")) {
          setLoading(false);
        }
      }
    };

    pickDefault();
  }, [user, accountsCatalog, initialAccountResolved, fetchReveal]);

  const handleStage2Submit = async () => {
    if (!seniorityTier || capabilityBets.length === 0) return;
    // Admin users bypass the live-required check (mirrors backend bypass).
    if (!user?.is_admin && !hasLiveInSelection(followedAccounts, accountsCatalog)) {
      setError("Select at least one account that is available now.");
      return;
    }
    setStage2Loading(true);
    try {
      await API.post("/intake/stage2", {
        seniority_tier: seniorityTier,
        capability_bets: capabilityBets,
        followed_accounts: followedAccounts,
      });
      updateUser({ intake_stage: 2 });
      setShowAnimation(true);
      setLoading(true);
      await fetchReveal();
      setTimeout(() => {
        setShowAnimation(false);
        setLoading(false);
      }, 8000);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setStage2Loading(false);
    }
  };

  const toggleCapability = (val) => {
    setCapabilityBets((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : prev.length < 3 ? [...prev, val] : prev
    );
  };

  const toggleFollowedAccount = (id) => {
    setFollowedAccounts((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  const toggleModalFollowed = (id) => {
    setModalFollowed((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  const handleModalSave = async () => {
    setModalError("");
    // Admin users bypass the live-required check (mirrors backend bypass).
    if (!user?.is_admin && !hasLiveInSelection(modalFollowed, accountsCatalog)) {
      setModalError("Select at least one account that is available now.");
      return;
    }
    setModalSaving(true);
    try {
      const { data } = await API.post("/intake/followed_accounts", {
        followed_accounts: modalFollowed,
      });
      setFollowedAccounts(data.followed_accounts || modalFollowed);
      setShowFollowModal(false);
    } catch (err) {
      setModalError(formatApiError(err));
    } finally {
      setModalSaving(false);
    }
  };

  // Wave 9.1 — revert to the previous combination captured in the recalib event
  const handleRevertRecalib = async () => {
    if (!recalibEvent?.prev) return;
    try {
      await API.put("/settings/profile", {
        role_family: recalibEvent.prev.role_family,
        seniority_tier: recalibEvent.prev.seniority_tier || null,
      });
      setRecalibEvent(null);
      toast.success("Reverted to your previous combination.");
      // refetch reveal so UI reflects the old cell
      setLoading(true);
      await fetchReveal();
      setLoading(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (user === undefined || (loading && !showAnimation && !showRecalibOverlay && !revealData)) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <p className="text-sm text-zinc-900">Loading...</p>
      </div>
    );
  }

  // Wave 9.1 — dedicated recalibration overlay (takes precedence over standard LoadingReveal)
  if (showRecalibOverlay) {
    const next = recalibEvent?.next || {};
    return (
      <RecalibratingOverlay
        summary={{
          roleLabel: next.role_family ? (ROLE_FAMILY_LABELS[next.role_family] || next.role_family) : "",
          seniorityLabel: next.seniority_tier ? (SENIORITY_LABELS[next.seniority_tier] || next.seniority_tier) : "",
        }}
        durationMs={3500}
        onComplete={() => setShowRecalibOverlay(false)}
      />
    );
  }

  if (showAnimation) {
    // Spec Screen 9 — pass dimmed chips for the user's followed accounts (names
    // only; no per-account progress, no AI theatrics). Falls back to whatever
    // account is currently selected if the followed list isn't loaded yet.
    const followed = followedAccounts || [];
    const chipNames = (followed.length ? followed : (selectedAccountId ? [selectedAccountId] : []))
      .map((id) => {
        const fromCatalog = accountsCatalog.find((a) => a.id === id)?.name;
        return fromCatalog || ACCOUNT_ID_TO_NAME[id] || id;
      })
      .filter(Boolean);
    return <LoadingReveal accountNames={chipNames} />;
  }

  const intake = revealData?.intake || {};
  const isPreview = revealData?.is_preview;
  const isStage2Complete = intake.stage === 2 || user?.intake_stage >= 2;
  let fullReveal = null;
  if (revealData?.full_reveal) {
    try { fullReveal = typeof revealData.full_reveal === "string" ? JSON.parse(revealData.full_reveal) : revealData.full_reveal; } catch { fullReveal = null; }
  }
  // Wave 12 Part 3 — surface per-reveal feedback-applied flag from the envelope into the reveal object
  if (fullReveal && revealData?.feedback_applied_on_this_reveal !== undefined) {
    fullReveal.feedback_applied_on_this_reveal = revealData.feedback_applied_on_this_reveal;
  }

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="intelligence-view">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-10">
        {/* 2026-05-03 — Wave 10.2 admin vendor ribbon REMOVED.
            Reason: redundancy with the universal "Viewing as:" saved-lens
            strip below the title divider — admins see that one too.
            One prominent place for the active lens, not three. */}

        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="block" data-testid="intelligence-logo-link" aria-label="Kalib AI home">
              <KalibLogo variant="light" height={28} testId="intelligence-header-logo" />
            </Link>
            <BetaBadge />
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/about"
              className="text-[11px] font-semibold uppercase tracking-widest text-zinc-700 hover:text-zinc-950 transition-colors hidden sm:inline"
              style={{ letterSpacing: "0.15em" }}
              data-testid="intelligence-principles-link"
            >
              Principles
            </Link>
            {user?.is_admin && (
              <Link to="/admin/accounts" className="text-[13px] text-amber-700 hover:text-amber-800 font-medium transition-colors" data-testid="intelligence-admin-link">
                Admin
              </Link>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex items-center gap-1.5 text-zinc-900 hover:text-zinc-950 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-700 px-1 py-0.5"
                data-testid="profile-menu-trigger"
                aria-label="Open profile menu"
              >
                <User className="w-4 h-4" strokeWidth={1.5} />
                {user?.first_name && (
                  <span className="text-[13px] font-medium text-zinc-900 hidden sm:inline max-w-[120px] truncate">
                    {user.first_name}
                  </span>
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="rounded-none border-zinc-200 min-w-[200px] py-1"
                data-testid="profile-menu-content"
              >
                <div className="px-3 py-2 border-b border-zinc-100">
                  <p className="text-[11px] uppercase tracking-widest text-zinc-700">Signed in as</p>
                  <p className="text-[13px] font-medium text-zinc-950 truncate">{user?.email}</p>
                </div>
                <DropdownMenuItem
                  onClick={() => navigate("/settings/profile")}
                  className="rounded-none cursor-pointer text-[13px] text-zinc-900 focus:bg-zinc-50 focus:text-zinc-950 py-2"
                  data-testid="profile-menu-reveal-setup"
                >
                  <Settings className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                  Reveal setup
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/settings/account")}
                  className="rounded-none cursor-pointer text-[13px] text-zinc-900 focus:bg-zinc-50 focus:text-zinc-950 py-2"
                  data-testid="profile-menu-account-settings"
                >
                  <KeyRound className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                  Account settings
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-zinc-100" />
                <DropdownMenuItem
                  onClick={() => { logout(); navigate("/"); }}
                  className="rounded-none cursor-pointer text-[13px] text-zinc-900 focus:bg-zinc-50 focus:text-zinc-950 py-2"
                  data-testid="profile-menu-signout"
                >
                  <LogOut className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Greeting */}
        <div className="mb-6" data-testid="greeting">
          <h1 className="text-2xl sm:text-3xl font-medium tracking-tight font-heading">
            <span className="text-zinc-950">{getGreeting()}, {revealData?.first_name || user?.first_name || "there"}. </span>
            <span style={{ color: "#B45309" }}>Here is your {revealData?.account_name || ACCOUNT_ID_TO_NAME[selectedAccountId] || "Deutsche Bank"} Reveal.</span>
          </h1>
        </div>

        {/* Wave 9.1 — recalibration banner (shown after settings save) */}
        {recalibEvent && (
          <RecalibrationBanner
            summary={{
              roleLabel: recalibEvent.next?.role_family ? (ROLE_FAMILY_LABELS[recalibEvent.next.role_family] || recalibEvent.next.role_family) : "",
              seniorityLabel: recalibEvent.next?.seniority_tier ? (SENIORITY_LABELS[recalibEvent.next.seniority_tier] || recalibEvent.next.seniority_tier) : "",
            }}
            changed={recalibEvent.changed || []}
            onRevert={handleRevertRecalib}
            onDismiss={() => setRecalibEvent(null)}
          />
        )}

        {/* Time mode toggle — sticky.
            v6.5.1: hidden behind SHOW_TIME_MODE_TOGGLE feature flag (NOT removed).
            All state (timeMode, handleModeChange, persisted preference via
            /api/me/time-mode) stays intact so flipping the flag restores
            the picker without further wiring. */}
        {SHOW_TIME_MODE_TOGGLE && (
          <div className={`sticky ${user?.is_admin ? "top-[77px]" : "top-[49px]"} z-10 mb-6 -mx-6 px-6 pt-2 pb-1 bg-zinc-100`} data-testid="time-mode-wrapper">
            <p className="text-sm text-zinc-900 mb-2">How much time do you have?</p>
            <TimeModeToggle mode={timeMode} setMode={handleModeChange} />
            <p className="text-[11px] text-zinc-900 mt-1 text-center">Your last choice is saved. Switch anytime.</p>
          </div>
        )}

        {/* Account tabs — Chunk 3: constrained to user's followed_accounts (max 3).
            Read-only catalog used for name + status lookup; presentation order
            follows the user's own followed_accounts list. */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="account-tabs">
          {(() => {
            const followedSet = new Set(Array.isArray(followedAccounts) ? followedAccounts : []);
            const followedTabs = accountsCatalog.filter((a) => followedSet.has(a.id)).slice(0, 3);
            // If the user has no followed_accounts (legacy / pre-modal), fall
            // back to showing the catalog so the page is still usable.
            const tabs = followedTabs.length > 0 ? followedTabs : accountsCatalog;
            return tabs.map((a) => {
              const isLive = a.status === "live";
              const isSelected = a.id === selectedAccountId;
              return (
                <button
                  key={a.id}
                  type="button"
                  disabled={!isLive}
                  onClick={() => isLive && handleAccountSwitch(a.id)}
                  className={`px-4 py-2 border text-sm font-medium transition-all ${
                    isSelected && isLive
                      ? "border-amber-700 bg-amber-700 text-white"
                      : isLive
                        ? "border-amber-700 bg-white text-zinc-950 hover:bg-amber-50"
                        : "border-zinc-200 bg-zinc-50 text-zinc-900 cursor-not-allowed"
                  }`}
                  data-testid={`tab-${a.id.replace(/_/g, '-')}`}
                  aria-pressed={isSelected}
                >
                  {a.name}
                  {!isLive && <Badge variant="secondary" className={`ml-2 text-xs rounded-none px-1 py-0 ${isSelected ? "bg-white/20 text-white" : ""}`}>Soon</Badge>}
                </button>
              );
            });
          })()}
        </div>

        {/* Account header */}
        <div className="bg-white border border-zinc-200 p-6 mb-6" data-testid="account-header">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-medium text-zinc-950 font-heading">{revealData?.account_name || ACCOUNT_ID_TO_NAME[selectedAccountId] || "Deutsche Bank"}</h2>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                {revealData?.generation_cycle && (
                  <p className="text-[13px] text-zinc-900" data-testid="week-identifier">{formatWeekRange(revealData.generation_cycle)}</p>
                )}
                {(() => {
                  // Chunk 3.9 — relative chip now reads the canonical
                  // reveal-freshness ISO (synthesis timestamp) rather
                  // than the harvest timestamp, so it cannot contradict
                  // the absolute "Updated DD Month" strip below which
                  // also derives from the synthesis cycle.
                  const freshIso = resolveRevealFreshnessIso(revealData);
                  const ageLabel = freshIso ? formatHarvestAge(freshIso) : null;
                  if (!ageLabel) return null;
                  const harvestStamp = revealData?.harvested_at
                    ? `Underlying public-signal research gathered on ${new Date(revealData.harvested_at).toLocaleString()}; calibrated live to your profile.`
                    : "Reveal calibrated live to your profile from the universal harvest.";
                  return (
                    <>
                      {revealData?.generation_cycle && <span className="text-[13px] text-zinc-900">&middot;</span>}
                      <p
                        className="text-[12px] text-zinc-900"
                        title={harvestStamp}
                        data-testid="harvest-age"
                      >
                        {ageLabel}
                      </p>
                    </>
                  );
                })()}
              </div>
            </div>
            <InlineCountdown nextCycle={revealData?.next_cycle} />
          </div>

          {/* Saved-lens strip + quiet last-updated cue (returning-user spec
              kalib-returning-user-signin-reveal-access-emergent-prompt.md).
              Subtle one-liner: "Viewing as: <offer> / <motion> / <seniority>
              / <capability>" + Change lens link + "Updated <date>" cue.
              Replaces the verbose "Your Reveal is calibrated for…" copy. */}
          {intake.company_type && (() => {
            const capLabel = (() => {
              const first = (intake.capability_bets || [])[0];
              if (!first) return null;
              const hit = CAPABILITY_OPTIONS.find((o) => o.value === first);
              return hit ? hit.label : null;
            })();
            return (
              <div
                className="mt-4 border-t border-zinc-200 pt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5"
                data-testid="calibration-strip"
              >
                <p className="text-[12px] text-zinc-700" data-testid="saved-lens-strip">
                  <span className="text-zinc-500">Viewing as:</span>{" "}
                  <span className="font-medium text-zinc-950">
                    {BREADCRUMB_TYPE_LABELS[intake.company_type] || COMPANY_TYPE_LABELS[intake.company_type] || intake.company_type}
                  </span>
                  {intake.role_family && (
                    <>
                      {" "}<span className="text-zinc-400">/</span>{" "}
                      <span className="font-medium text-zinc-950">
                        {ROLE_FAMILY_LABELS[intake.role_family] || intake.role_family}
                      </span>
                    </>
                  )}
                  {intake.seniority_tier && (
                    <>
                      {" "}<span className="text-zinc-400">/</span>{" "}
                      <span className="font-medium text-zinc-950">
                        {SENIORITY_LABELS[intake.seniority_tier] || intake.seniority_tier}
                      </span>
                    </>
                  )}
                  {capLabel && (
                    <>
                      {" "}<span className="text-zinc-400">/</span>{" "}
                      <span className="font-medium text-zinc-950">{capLabel}</span>
                    </>
                  )}
                </p>
                <Link
                  to="/settings/profile"
                  className="text-[12px] text-amber-700 hover:text-amber-800 font-medium transition-colors"
                  data-testid="change-lens-link"
                >
                  Change lens
                </Link>
                {/* Chunk 3.11 (2026-05-05) — Fix 6 freshness standardisation.
                    The "Updated DD Mon" date was duplicating the relative
                    chip in the page header (both derived from
                    revealData.reveal_date). Drop the date on the strip;
                    keep only the "Viewing your saved lens" cue so users
                    have one canonical freshness label per page. */}
                <span
                  className="ml-auto text-[12px] text-zinc-500"
                  data-testid="saved-lens-cue"
                >
                  Viewing your saved lens
                </span>
              </div>
            );
          })()}
        </div>

        {/* Chunk 3 — Persona acknowledgement strip removed per acceptance criterion 5.
            (Was: italic muted-grey "Your existing advisory mandate must be re-anchored…" line.) */}

        {/* Chunk 1 / Task 4 — 503 calibration error soft state */}
        {error && error.includes("calibration_error") ? (
          <div
            className="bg-amber-50 border border-amber-200 px-5 py-4 mb-6 text-sm text-amber-800"
            data-testid="calibration-error-soft-state"
          >
            Reveal in progress — refresh in a minute.
          </div>
        ) : error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-4 py-3 mb-6" data-testid="intelligence-error">{error}</div>
        )}

        {/* Lens breadcrumb removed (2026-05-08) — duplicated the saved-lens
            strip above ("Viewing as: …  Change lens") which already
            displays the same info more prominently. */}

        {/* Signal sentence */}
        <div className="bg-white border-l-4 border-l-amber-700 border border-zinc-200 p-6 mb-6" data-testid="signal-sentence">
          <p className="uppercase mb-2" style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em", color: "#B45309" }}>Signal this week</p>
          <p className="font-heading font-medium" style={{ fontSize: "22px", lineHeight: 1.5, color: "#111111", fontWeight: 500 }}>
            <span className="md:hidden" style={{ fontSize: "18px" }}>{revealData?.signal_sentence || "Loading signal..."}</span>
            <span className="hidden md:inline">{revealData?.signal_sentence || "Loading signal..."}</span>
          </p>
          {/* SIGNAL_DISCIPLINE Rule 3 (Beta P1, 2026-05-23) — italic
              disclosure deleted from below the signal sentence.
              Single canonical placement is the ACTIONS THIS WEEK header
              in RevealRenderer. Keeping the disclosure in one place only
              prevents the contradiction the rule was written to avoid
              (card count + disclaimer drift). */}
        </div>

        {/* Chunk 3 — External view marker block removed. Its content is now folded
            into the action cards (via body_markdown bridge sentences) and the
            Sources list (via citation chips). */}

        {/* Wave 3l — stage 2 strip removed. Intake now collects every axis
            upfront at /intake; users only land here once stage 2 is complete.
            Legacy stage-2 users (intake_stage < 2) are bounced to /intake by
            the redirect below. */}
        {!isStage2Complete && (() => { navigate("/intake", { replace: true }); return null; })()}

        {/* Full Reveal — unlocked for all users during trial */}
        {fullReveal && <RevealRenderer
          reveal={fullReveal}
          accountName={revealData?.account_name || ACCOUNT_ID_TO_NAME[selectedAccountId] || "Deutsche Bank"}
          weekLabel={formatWeekRange(revealData?.generation_cycle) || ""}
          mode={timeMode}
          accountId={selectedAccountId}
          revealCycle={revealData?.generation_cycle || ""}
          isLite={revealData?.is_lite === true}
        />}
      </div>

      {/* Wave 8 — one-time modal for existing users missing followed_accounts */}
      <Dialog open={showFollowModal} onOpenChange={(open) => { if (!open && !modalSaving) setShowFollowModal(false); }}>
        <DialogContent className="rounded-none border-zinc-200 max-w-lg" data-testid="follow-accounts-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-zinc-950">Which accounts do you want Reveals for?</DialogTitle>
            <DialogDescription className="text-sm text-zinc-900">
              Deutsche Bank is live today. Follow the others and we'll email you the moment they launch — no spam, no newsletter.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <AccountFollowList
              accounts={accountsCatalog}
              selected={modalFollowed}
              onToggle={toggleModalFollowed}
              testIdPrefix="modal"
            />
          </div>
          {modalError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="modal-error">{modalError}</p>
          )}
          <DialogFooter>
            <Button
              onClick={handleModalSave}
              disabled={modalSaving || (!user?.is_admin && !hasLiveInSelection(modalFollowed, accountsCatalog))}
              className="bg-amber-700 text-white hover:bg-amber-800 rounded-none text-sm font-medium px-6 py-2 h-auto disabled:opacity-40"
              data-testid="modal-save-btn"
            >
              {modalSaving ? "Saving..." : "Save preferences"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
